<template>
  <div class="h-full bg-muted">
    <div
      class="flex flex-col items-center justify-center w-full pb-20 bg-muted"
    >
      <section class="flex flex-col items-center w-full justify-center">
        <!-- Combined Header, Stats, and Filters Section -->
        <div
          class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full"
        >
          <!-- Left Side: Page Title & Stats -->
          <div class="items-center gap-2 px-4 -ml-2 d-flex">
            <img
              src="@/assets/img/sidebar/icons8-worldwide-location-50.png"
              class="cursor-pointer w-6 md:w-[28px]"
              alt="Locations"
            />
            <div class="flex items-center gap-1">
              <p class="text-base md:text-[22px] font-bold">Locations</p>
            </div>
          </div>

          <!-- Right Side: Action Buttons - Pushed to the absolute far right -->
          <div
            class="flex items-center ml-auto justify-end gap-2 mt-5 mr-0 md:mr-6"
          >
            <div class="d-flex items-center space-x-2">
              <v-menu offset-y v-model="isDropdownOpen" bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    class="flex items-center gap-1 px-3 py-1 text-primary hover:bg-slate-100 rounded-md font-medium transition-colors"
                    style="font-size: 16px"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small color="primary"
                      >mdi-plus-circle-outline</v-icon
                    >
                    <span class="hidden sm:inline" style="font-size: 16px"
                      >Add Locations</span
                    >
                    <v-icon
                      small
                      color="primary"
                      :class="{ 'rotate-180': isDropdownOpen }"
                      class="transition-transform duration-300"
                    >
                      mdi-chevron-down
                    </v-icon>
                  </button>
                </template>
                <v-list dense>
                  <v-list-item
                    @click="
                      showInviteForm = true;
                      activeTab = 1;
                    "
                  >
                    <v-list-item-icon class="mr-2 -ml-1"
                      ><v-icon small>mdi-pencil</v-icon></v-list-item-icon
                    >
                    <v-list-item-title class="text-base"
                      >Manually add new location</v-list-item-title
                    >
                  </v-list-item>
                  <v-list-item :loading="isLoading" @click="handleBulkAddClick">
                    <v-list-item-icon class="mr-2 -ml-1"
                      ><v-icon small
                        >mdi-database-import</v-icon
                      ></v-list-item-icon
                    >
                    <v-list-item-title class="text-base"
                      >Connect via Google</v-list-item-title
                    >
                  </v-list-item>
                  <v-list-item
                    @click="
                      mapsSearchForm = true;
                      activeTab = 1;
                    "
                  >
                    <v-list-item-icon class="mr-2 -ml-1"
                      ><v-icon small>mdi-map-marker</v-icon></v-list-item-icon
                    >
                    <v-list-item-title class="text-base"
                      >Add via Google Maps</v-list-item-title
                    >
                  </v-list-item>
                </v-list>
              </v-menu>

              <button
                class="flex items-center gap-1 px-3 py-1 text-primary hover:bg-slate-100 rounded-md font-medium transition-colors"
                style="font-size: 16px"
                @click="showUploadDialog = true"
              >
                <v-icon small color="primary">mdi-upload-outline</v-icon>
                <span class="hidden sm:inline" style="font-size: 16px"
                  >Upload Locations</span
                >
              </button>
            </div>
          </div>
        </div>

        <!-- Tabs for Dashboard and Locations - Now left aligned -->
        <div class="w-full mt-4">
          <v-tabs
            v-model="activeTab"
            background-color="white"
            color="primary"
            slider-color="primary"
            align-with-title
            grow
            class="elevation-1 rounded-t-lg"
          >
            <v-tab class="px-6 justify-start text-base">
              <v-icon left small>mdi-view-dashboard</v-icon>
              Dashboard
            </v-tab>
            <v-tab class="px-6 justify-start text-base">
              <v-icon left small>mdi-map-marker-multiple</v-icon>
              Locations
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" class="mt-1">
            <!-- Dashboard Tab -->
            <v-tab-item>
              <div class="bg-white py-4 rounded-b-lg shadow-md">
                <!-- Dashboard Component -->
                <LocationsSummaryDashboard
                  :locations="invitedMembers"
                  @filter-status="filterByStatus"
                  v-if="!showInviteForm && !mapsSearchForm"
                />
              </div>
            </v-tab-item>

            <!-- Locations Tab -->
            <v-tab-item>
              <div class="bg-white rounded-b-lg shadow-md">
                <!-- Filters Row -->
                <div class="w-full px-4 py-3 bg-light shadow-sm">
                  <div
                    class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-3"
                  >
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <div
                        class="search-container flex-grow min-w-[200px] sm:flex-grow-0 sm:w-auto"
                      >
                        <input
                          type="text"
                          v-model="searchQuery"
                          placeholder="Search Locations"
                          class="search-input w-full text-sm px-3 py-1.5 border border-slate-300 rounded-md focus:ring-primary focus:border-primary"
                        />
                      </div>

                      <!-- Replace status filter buttons with dropdown -->
                      <v-select
                        v-model="selectedStatus"
                        :items="[
                          { text: 'All', value: null },
                          { text: 'Connected', value: 'connected' },
                          { text: 'Shallow Connected', value: 'shallow' },
                          { text: 'Verified', value: 'verified' },
                          { text: 'Unverified', value: 'unverified' },
                          { text: 'Auth Required', value: 'auth_required' },
                        ]"
                        item-text="text"
                        item-value="value"
                        label="Status Filter"
                        dense
                        outlined
                        hide-details
                        class="status-filter-dropdown text-xs min-w-[150px] max-w-[180px]"
                        background-color="white"
                        append-icon="mdi-chevron-down"
                      ></v-select>

                      <v-select
                        v-model="selectedRating"
                        :items="ratingOptions"
                        item-text="text"
                        item-value="value"
                        label="All Ratings"
                        dense
                        outlined
                        hide-details
                        class="rating-filter-v-select text-xs min-w-[150px] max-w-[180px]"
                        background-color="white"
                        append-icon="mdi-chevron-down"
                      ></v-select>
                    </div>

                    <!-- Right aligned buttons -->
                    <div class="flex items-center">
                      <!-- Moved Sort Dropdown Here -->
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            text
                            small
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            class="text-sm md:text-base font-medium text-primary"
                            style="font-size: 15px !important"
                          >
                            <v-icon left small color="primary"
                              >mdi-sort-variant</v-icon
                            >
                            <span class="text-primary" style="font-size: 14px"
                              >Sort by: {{ selectedSortDescription }}</span
                            >
                            <v-icon right small color="primary"
                              >mdi-chevron-down</v-icon
                            >
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item
                            v-for="(option, index) in sortOptions"
                            :key="index"
                            @click="applySort(option)"
                          >
                            <v-list-item-title
                              class="text-sm md:text-base"
                              style="font-size: 15px !important"
                              >{{ option.text }}</v-list-item-title
                            >
                          </v-list-item>
                        </v-list>
                      </v-menu>

                      <v-btn
                        icon
                        small
                        @click="fetchStores"
                        class="ml-1 items-center text-slate-600 hover:text-primary"
                      >
                        <v-icon>mdi-refresh</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Main Content Section -->
                <div class="w-full">
                  <div class="flex flex-col gap-2 p-4">
                    <!-- Invite Member Form -->
                    <transition name="form-fade">
                      <section class="invite-section" v-show="showInviteForm">
                        <div class="flex justify-between items-center mb-4">
                          <h3 class="text-xl font-bold">Add New Location</h3>
                          <v-btn icon small @click="closeInviteForm">
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </div>

                        <form @submit.prevent="addMember" class="invite-form">
                          <div class="form-group">
                            <input
                              ref="autocomplete"
                              type="text"
                              placeholder="Enter store location"
                              v-model="storeLocation"
                              class="input-field"
                              required
                            />
                          </div>
                          <div class="form-row">
                            <input
                              type="text"
                              v-model="sub_locality"
                              class="input-field"
                              placeholder="Locality"
                              required
                            />
                            <input
                              type="text"
                              v-model="city"
                              class="input-field"
                              placeholder="City"
                              required
                            />
                            <input
                              type="text"
                              v-model="state"
                              class="input-field"
                              placeholder="State"
                              required
                            />
                            <input
                              type="text"
                              v-model="zipcode"
                              class="input-field"
                              placeholder="Zip Code"
                              required
                            />
                          </div>
                          <div class="form-checkbox">
                            <label class="checkbox-label">
                              <input
                                type="checkbox"
                                v-model="sendInvitation"
                                class="checkbox-input"
                              />
                              <span class="checkbox-text"
                                >Send invitation for this location</span
                              >
                            </label>
                          </div>
                          <div class="form-row">
                            <input
                              type="email"
                              v-model="email"
                              class="input-field"
                              placeholder="Email"
                              :required="sendInvitation"
                              :disabled="!sendInvitation"
                            />
                            <input
                              type="tel"
                              v-model="phone"
                              class="input-field"
                              placeholder="Phone Number"
                              :required="sendInvitation"
                              :disabled="!sendInvitation"
                            />
                          </div>
                          <div class="form-group">
                            <v-btn type="submit" class="btn-primary"
                              >Add Location</v-btn
                            >
                          </div>

                          <!-- Success Message and Options as Text Links -->
                          <div
                            v-if="locationAdded"
                            class="success-options mt-4 flex gap-2 items-center"
                          >
                            <span class="text-black-500 font-semibold"
                              >Location successfully added</span
                            >
                            <span
                              class="text-red-600 cursor-pointer font-semibold"
                              @click="closeSection"
                              >OK</span
                            >
                            <span
                              class="text-[#8050DE] cursor-pointer font-semibold"
                              @click="addMore"
                              >Add More</span
                            >
                          </div>
                        </form>
                      </section>
                    </transition>

                    <!-- Shallow Connect Form -->
                    <transition name="form-fade">
                      <section class="invite-section" v-show="mapsSearchForm">
                        <div
                          class="flex justify-between items-center mb-4 -mt-2"
                        >
                          <h3 class="text-xl font-bold">
                            Search on Google Maps
                          </h3>
                          <div>
                            <div class="flex items-center gap-2">
                              <div
                                class="text-sm text-gray-500"
                                v-if="isBusinessLoading"
                              >
                                Fetching in progress
                                <v-progress-circular
                                  indeterminate
                                  color="primary"
                                  size="16"
                                  width="2"
                                  class="ml-2"
                                ></v-progress-circular>
                              </div>
                              <v-btn icon small @click="handleMapsCloseBtn">
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </div>
                          </div>
                        </div>

                        <form
                          @submit.prevent="searchBusinesses"
                          class="invite-form"
                        >
                          <div class="form-group flex gap-3 items-center">
                            <input
                              type="text"
                              placeholder="Enter store location"
                              v-model="businessSearchTerm"
                              class="input-field"
                              required
                            />
                            <div class="w-1/4">
                              <!-- Country Selector -->
                              <v-select
                                v-model="selectedCountry"
                                :items="countries"
                                item-text="text"
                                item-value="value"
                                label="Select Country"
                                outlined
                                dense
                                :rules="rules"
                                hide-details
                                required
                                :error-messages="countryError"
                                :error="!!countryError"
                              ></v-select>
                            </div>
                            <div class="w-1/4">
                              <!-- Zone Selector -->
                              <v-select
                                v-model="selectedZone"
                                :items="zones"
                                item-text="text"
                                item-value="value"
                                label="Select Zone"
                                outlined
                                dense
                                :rules="rules"
                                hide-details
                                required
                                :error-messages="zoneError"
                                :error="!!zoneError"
                              ></v-select>
                            </div>
                            <div class="w-1/6">
                              <!-- Max Results Input -->
                              <input
                                type="number"
                                v-model="maxResults"
                                placeholder="Max Results (optional)"
                                class="input-field"
                                min="1"
                                max="100"
                              />
                            </div>
                            <div>
                              <v-btn
                                depressed
                                color="primary"
                                type="submit"
                                :disabled="isSearching"
                              >
                                Search
                              </v-btn>
                            </div>
                          </div>
                          <div class="form-group"></div>
                        </form>

                        <div class="search-results-container max-h-[60vh]">
                          <!-- <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular
                              v-if="isSearching"
                              indeterminate
                              color="primary"
                              class="text-center"
                            ></v-progress-circular>
                          </v-row> -->
                          <div
                            v-if="searchResults.length"
                            class="flex justify-between items-center mt-4 mb-2 w-full"
                          >
                            <span class="text-gray-600"
                              >{{ searchResults.length }} locations found</span
                            >
                            <div>
                              <div class="flex items-center gap-2">
                                <v-checkbox
                                  v-model="selectAll"
                                  label="Select All"
                                  @change="toggleSelectAll"
                                  dense
                                  hide-details
                                ></v-checkbox>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div
                              class="flex flex-col items-start gap-1"
                              v-if="searchResults.length"
                            >
                              <div
                                class="flex justify-between items-center w-full"
                              >
                                <div class="relative w-full md:w-1/2">
                                  <input
                                    type="text"
                                    v-model="businessSearchFilter"
                                    placeholder="Filter businesses..."
                                    class="input-field py-2 border border-gray-300 rounded-md pr-8"
                                  />
                                  <button
                                    v-if="businessSearchFilter"
                                    @click="businessSearchFilter = ''"
                                    class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                  >
                                    <v-icon small>mdi-close</v-icon>
                                  </button>
                                </div>
                                <div class="relative w-full md:w-1/3">
                                  <v-select
                                    v-model="selectedBusinessSortOption"
                                    :items="businessSortOptions"
                                    item-text="text"
                                    item-value="value"
                                    label="Sort by"
                                    outlined
                                    dense
                                    hide-details
                                    return-object
                                    class="text-sm"
                                  ></v-select>
                                </div>
                              </div>
                              <span
                                class="text-gray-500 text-sm"
                                v-if="
                                  searchResults.length &&
                                  filteredBusinesses.length <
                                    searchResults.length
                                "
                                >{{ filteredBusinesses.length }} search
                                results</span
                              >
                            </div>
                          </div>
                          <div
                            class="flex flex-col gap-2 max-h-[400px] overflow-y-auto px-4 mt-2 pt-4"
                          >
                            <div
                              v-for="(business, index) in filteredBusinesses"
                              :key="index"
                              class="business-card p-4 mb-3 border rounded-lg hover:bg-gray-50"
                            >
                              <div
                                class="flex justify-between items-start gap-3"
                              >
                                <div class="flex items-start gap-3">
                                  <v-checkbox
                                    v-model="business.selected"
                                    :disabled="business.disabled"
                                    dense
                                    hide-details
                                    class="mt-1"
                                  ></v-checkbox>
                                  <div>
                                    <h4 class="font-semibold text-lg">
                                      <a
                                        v-if="business.map_link"
                                        :href="business.map_link"
                                        target="_blank"
                                        class="text-[#4316a2] hover:text-[#5b29c1] hover:underline"
                                      >
                                        {{ business.name }}
                                      </a>
                                      <template v-else>
                                        {{ business.name }}
                                      </template>
                                    </h4>
                                    <p class="text-gray-600 text-sm mt-1">
                                      {{ business.address }}
                                    </p>
                                    <p
                                      v-if="
                                        business.display_link || business.link
                                      "
                                      class="text-gray-600 text-sm mt-1"
                                    >
                                      Website:
                                      <a
                                        :href="business.link"
                                        target="_blank"
                                        class="text-[#4316a2] hover:text-[#5b29c1] hover:underline"
                                        >{{
                                          business.display_link
                                            ? business.display_link
                                            : business.link
                                        }}</a
                                      >
                                    </p>
                                    <p
                                      class="text-sm mt-1"
                                      v-if="business.business_status"
                                      :class="
                                        business.business_status ==
                                        'OPERATIONAL'
                                          ? 'text-green-600'
                                          : 'text-red-600'
                                      "
                                    >
                                      {{
                                        business.business_status ==
                                        "OPERATIONAL"
                                          ? "Operational"
                                          : business.business_status ==
                                            "CLOSED_PERMANENTLY"
                                          ? "Closed Permanently"
                                          : business.business_status ==
                                            "CLOSED_TEMPORARILY"
                                          ? "Closed Temporarily"
                                          : business.business_status
                                      }}
                                    </p>
                                    <div class="flex items-center gap-4 mt-2">
                                      <div class="flex items-center">
                                        <v-icon small color="amber"
                                          >mdi-star</v-icon
                                        >
                                        <span class="ml-1"
                                          >{{ business.rating }}
                                          <span v-if="business.reviews">
                                            ({{
                                              business.reviews
                                            }}
                                            reviews)</span
                                          ></span
                                        >
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <v-btn
                                  @click="addBusinessLocation(business)"
                                  :disabled="business.disabled"
                                  dense
                                  outlined
                                  color="success"
                                >
                                  Add Location
                                </v-btn>
                              </div>
                            </div>
                          </div>
                          <div
                            class="flex justify-center mt-3"
                            v-if="searchResults?.length > 0"
                          >
                            <v-btn
                              type="submit"
                              :disabled="!hasSelectedLocations && !isSearching"
                              @click="addSelectedLocations"
                              color="success"
                              outlined
                            >
                              Add
                              {{
                                selectedLocationsCount
                                  ? selectedLocationsCount
                                  : ""
                              }}
                              Selected Location{{
                                selectedLocationsCount > 1 ? "s" : ""
                              }}
                            </v-btn>
                          </div>
                        </div>
                      </section>
                    </transition>

                    <!-- Invited Members Table -->
                    <section class="table-section">
                      <div class="table-responsive flex-grow overflow-x-auto">
                        <!-- Grid layout for LocationListItems -->
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 py-2">
                          <LocationListItem
                            v-for="(member, index) in paginatedMembers"
                            :key="index"
                            :member="member"
                            :platformLogoCache="platformLogoCache"
                            @delete="deleteMember"
                            @resend-invitation="resendInvitation"
                            @open-invitation-link="openInvitationLink"
                            @view-profile="handleViewProfile"
                          />
                        </div>
                        <!-- No Results Found -->
                        <div
                          v-if="isLoading"
                          class="loading-message py-10 text-center"
                        >
                          <img
                            width="25"
                            height="25"
                            src="https://img.icons8.com/ios-filled/50/8056de/worldwide-location.png"
                            alt="worldwide-location"
                            class="inline-block mr-2"
                          /><span class="text-xl font-medium"
                            >Fetching locations...</span
                          >
                        </div>
                        <div
                          v-else-if="invitedMembers.length === 0 && !isLoading"
                          class="empty-state py-10 px-6 text-center"
                        >
                          <NoLocationsConnected
                            title="No Google Business Locations Connected"
                            message="Connect your Google Business locations to manage all your business profiles in one place. Monitor reviews, update business information, and track performance metrics easily."
                            actionText="Connect Google Locations"
                            @click.native="handleBulkAdd"
                          />
                        </div>
                      </div>
                      <!-- Closing table-responsive -->

                      <!-- Pagination Controls -->
                      <div
                        v-if="filteredMembers?.length > itemsPerPage"
                        class="pagination-controls flex items-center justify-center space-x-4 mt-6 py-4 border-t border-slate-200"
                      >
                        <!-- Previous Button -->
                        <button
                          @click="prevPage"
                          :disabled="currentPage === 1"
                          class="pagination-button px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-all"
                        >
                          Previous
                        </button>

                        <!-- Page Indicator -->
                        <span class="text-gray-700 font-medium">
                          Page {{ currentPage }} of
                          {{ Math.ceil(totalMembers / itemsPerPage) }}
                        </span>

                        <!-- Next Button -->
                        <button
                          @click="nextPage"
                          :disabled="
                            currentPage ===
                            Math.ceil(totalMembers / itemsPerPage)
                          "
                          class="pagination-button px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-all"
                        >
                          Next
                        </button>
                      </div>
                    </section>
                    <!-- Closing table-section -->
                  </div>
                  <!-- Closing div with p-4 -->
                </div>
                <!-- Closing main content section div -->
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </section>
      <!-- Closing w-full justify-center section -->
    </div>
    <!-- Closing pb-20 bg-muted div -->

    <v-dialog v-model="showUploadDialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Upload Locations CSV</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              showUploadDialog = false;
              uploadResults = null;
              uploadProgress = null;
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="!isUploading && !uploadResults">
            <p>
              Upload a CSV file with location data.
              <a
                href="#"
                @click.prevent="downloadSampleCSV"
                class="text-primary hover:underline font-medium"
                >Download Sample CSV</a
              >
            </p>
            <input
              type="file"
              @change="handleFileUpload"
              accept=".csv"
              class="mt-4 block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-primary hover:file:bg-violet-100"
            />
          </div>
          <div v-if="isUploading" class="upload-progress text-center py-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
            ></v-progress-circular>
            <p class="mt-3 text-lg font-medium text-primary">
              Uploading...
              <span v-if="uploadProgress && uploadProgress.total > 0">
                ({{ uploadProgress.current }} / {{ uploadProgress.total }})
              </span>
            </p>
          </div>
          <div
            v-if="uploadResults"
            class="upload-results mt-4 p-4 border border-slate-200 rounded-md bg-slate-50"
          >
            <h4 class="text-lg font-semibold mb-2 text-slate-700">
              Upload Complete
            </h4>
            <div class="space-y-1 text-sm">
              <p>
                <span class="font-medium text-green-600"
                  >Successfully uploaded:</span
                >
                {{ uploadResults.success }}
              </p>
              <p>
                <span class="font-medium text-amber-600"
                  >Skipped (duplicates/existing):</span
                >
                {{ uploadResults.skipped }}
              </p>
              <p>
                <span class="font-medium text-red-600">Errors:</span>
                {{ uploadResults.errors }}
              </p>
            </div>
            <div
              v-if="
                uploadResults.errors > 0 &&
                uploadResults.errorDetails &&
                uploadResults.errorDetails.length > 0
              "
              class="mt-3 text-xs text-red-700"
            >
              <p class="font-semibold mb-1">Error Details:</p>
              <ul
                class="list-disc list-inside max-h-40 overflow-y-auto bg-red-50 p-2 rounded"
              >
                <li
                  v-for="(error, index) in uploadResults.errorDetails"
                  :key="index"
                >
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="
              showUploadDialog = false;
              uploadResults = null;
              uploadProgress = null;
            "
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Location Profile Drawer -->
    <LocationProfileDrawer
      v-if="selectedLocationId"
      v-model="showLocationProfileDrawer"
      :locationId="selectedLocationId"
      :userId="currentUID"
    />
  </div>
  <!-- Closing h-full bg-muted div -->
</template>
<script>
import { auth, colUsers, db, functions } from "@/utils/firebase.utils";
import {
  getDoc,
  getDocs,
  addDoc,
  collection,
  doc,
  query,
  updateDoc,
  where,
  Timestamp,
  deleteDoc,
  onSnapshot,
  limit,
  increment,
  setDoc,
  arrayUnion,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import LocationListItem from "./LocationListItem.vue";
import LocationProfileDrawer from "./LocationProfileDrawer.vue";
import rulesConstants from "@/assets/constants/rules.constants";
import NoLocationsConnected from "@/components/ui/common/NoLocationsConnected.vue";
import LocationsSummaryDashboard from "@/components/dashboards/LocationsSummaryDashboard.vue";
import { v4 as uuidv4 } from "uuid";
// import ToolTip from "./"
export default {
  components: {
    LocationListItem,
    LocationProfileDrawer,
    NoLocationsConnected,
    LocationsSummaryDashboard,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      activeTab: 0, // 0 for Dashboard, 1 for Locations
      showInviteForm: false,
      locationAdded: false,
      storeLocation: "",
      state: "",
      city: "",
      email: "",
      phone: "",
      zipcode: "",
      country: "",
      name: "",
      sub_locality: "",
      invitationStatus: "invited",
      invitedMembers: [], // All members
      currentPage: 1, // Current page number
      itemsPerPage: 10, // Number of items per page
      totalMembers: 0, // Total number of members
      dialog: false, // Control dialog visibility
      editedMember: {}, // Member being edited
      editedIndex: null, // Index of member being edited
      statusOptions: ["invited", "accepted"],
      geoLocation: "",
      addingNew: false,
      unsubscribeStore: null,
      searchQuery: "", // Search query input by the user
      platformLogoCache: {}, // Cache for platform logos
      selectedStatus: null,
      availableStatuses: ["invited", "accepted", "connected", "disconnected"],
      showUploadDialog: false,
      isUploading: false,
      uploadProgress: null,
      uploadResults: null,
      showEditDialog: false,
      editedLocation: {},
      isSaving: false,
      activeFilters: {
        city: null,
        state: null,
        country: null,
        filter: null,
        filter_ascending: false,
      },
      locationRatings: {},
      sendInvitation: false,
      isLoading: false,
      isDropdownOpen: false,
      selectedRating: "", // Initialize to an empty string for "All Ratings"
      businessSearchTerm: "",
      mapsSearchForm: false,
      searchResults: [],
      isSearching: false,
      hasSearched: false,
      selectAll: false,
      selectedZone: "",
      zones: [
        { text: "North", value: "north" },
        { text: "South", value: "south" },
        { text: "East", value: "east" },
        { text: "West", value: "west" },
        { text: "Central", value: "central" },
      ],
      rules: [rulesConstants.required],
      zoneError: "",
      countryError: "",
      jobUnsubscribe: null,
      resultsUnsubscribe: null,
      isBusinessLoading: false,
      businessSearchFilter: "",
      hasShallowBusinesses: false,
      selectedCountry: "", // New data property for selected country
      countries: [
        { text: "India", value: "india" },
        { text: "UK", value: "uk" },
        { text: "US", value: "us" },
        { text: "Australia", value: "au" },
        { text: "Germany", value: "de" },
        { text: "United Arab Emirates", value: "uae" },
      ],
      ratingOptions: [
        { text: "All Ratings", value: "" },
        { text: "1★ - 2★", value: "1-2" },
        { text: "2★ - 3★", value: "2-3" },
        { text: "3★ - 4★", value: "3-4" },
        { text: "4★ - 5★", value: "4-5" },
      ],
      selectedSortOption: { value: "name_asc", text: "Name (A to Z)" }, // Default sort
      sortOptions: [
        {
          value: "name_asc",
          text: "Name (A to Z)",
          field: "name",
          ascending: true,
        },
        {
          value: "name_desc",
          text: "Name (Z to A)",
          field: "name",
          ascending: false,
        },
        {
          value: "rating_desc",
          text: "Rating - High to Low",
          field: "averageRating",
          ascending: false,
        },
        {
          value: "rating_asc",
          text: "Rating - Low to High",
          field: "averageRating",
          ascending: true,
        },
        {
          value: "reviews_desc",
          text: "Reviews - High to Low",
          field: "reviewCount",
          ascending: false,
        },
        {
          value: "reviews_asc",
          text: "Reviews - Low to High",
          field: "reviewCount",
          ascending: true,
        },
        {
          value: "health_desc",
          text: "Profile Health - High to Low",
          field: "profile_strength",
          ascending: false,
        },
        {
          value: "health_asc",
          text: "Profile Health - Low to High",
          field: "profile_strength",
          ascending: true,
        },
      ],
      selectedBusinessSortOption: null,
      businessSortOptions: [
        {
          value: "rating_desc",
          text: "Rating - High to Low",
          field: "rating",
          ascending: false,
        },
        {
          value: "rating_asc",
          text: "Rating - Low to High",
          field: "rating",
          ascending: true,
        },
        {
          value: "reviews_desc",
          text: "Reviews - High to Low",
          field: "reviews",
          ascending: false,
        },
        {
          value: "reviews_asc",
          text: "Reviews - Low to High",
          field: "reviews",
          ascending: true,
        },
      ],

      // Location Profile Drawer
      showLocationProfileDrawer: false,
      selectedLocationId: null,
      maxResults: null,
    };
  },
  computed: {
    filteredBusinesses() {
      let businesses = [...this.searchResults];

      if (this.businessSearchFilter) {
        const searchTerm = this.businessSearchFilter.toLowerCase().trim();
        businesses = businesses.filter((business) => {
          return (
            (business.name &&
              business.name.toLowerCase().includes(searchTerm)) ||
            (business.address &&
              business.address.toLowerCase().includes(searchTerm)) ||
            (business.link && business.link.toLowerCase().includes(searchTerm))
          );
        });
      }
      // Apply sorting if an option is selected
      if (
        this.selectedBusinessSortOption &&
        this.selectedBusinessSortOption.value
      ) {
        const { field, ascending } = this.selectedBusinessSortOption;
        businesses.sort((a, b) => {
          const valA = a[field] || 0; // Default to 0 if field is missing
          const valB = b[field] || 0; // Default to 0 if field is missing

          if (typeof valA === "string" && typeof valB === "string") {
            return ascending
              ? valA.localeCompare(valB)
              : valB.localeCompare(valA);
          }
          return ascending ? valA - valB : valB - valA;
        });
      }

      return businesses;
    },
    selectedLocationsCount() {
      return (
        this.filteredBusinesses?.filter(
          (business) => business.selected && !business.disabled
        ).length || 0
      );
    },
    hasSelectedLocations() {
      return this.filteredBusinesses?.length
        ? this.filteredBusinesses.some((business) => business.selected)
        : false;
    },
    filteredMembers() {
      let filtered = this.invitedMembers;

      // 1) Apply city/state/country filters:
      if (this.activeFilters.city) {
        filtered = filtered.filter(
          (member) => member.city === this.activeFilters.city
        );
      }
      if (this.activeFilters.state) {
        filtered = filtered.filter(
          (member) => member.state === this.activeFilters.state
        );
      }
      if (this.activeFilters.country) {
        filtered = filtered.filter(
          (member) => member.country === this.activeFilters.country
        );
      }

      // 2) Apply status filter:
      if (this.selectedStatus) {
        switch (this.selectedStatus) {
          case "shallow":
            filtered = filtered.filter(
              (member) => member.shallowConnect === true
            );
            break;
          case "verified":
            filtered = filtered.filter(
              (member) => member.gmbProfile?.verificationState === "COMPLETED"
            );
            break;
          case "unverified":
            filtered = filtered.filter(
              (member) =>
                member.gmbProfile?.verificationState === "UNVERIFIED" ||
                member.gmbProfile?.verificationState === "FAILED"
            );
            break;
          case "auth_required":
            filtered = filtered.filter(
              (member) => member.needs_gmb_reauth === true
            );
            break;
          case "connected":
            filtered = filtered.filter(
              (member) => member.status === "connected"
            );
            break;
          default:
            filtered = filtered.filter(
              (member) => member.status === this.selectedStatus
            );
            break;
        }
      }

      // 3) Apply search filter:
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((member) => {
          return (
            (member.storeLocation &&
              member.storeLocation.toLowerCase().includes(query)) ||
            (member.city && member.city.toLowerCase().includes(query)) ||
            (member.state && member.state.toLowerCase().includes(query)) ||
            (member.zipcode && member.zipcode.toLowerCase().includes(query)) ||
            (member.country && member.country.toLowerCase().includes(query)) ||
            (member.email && member.email.toLowerCase().includes(query)) ||
            (member.phone && member.phone.toLowerCase().includes(query)) ||
            (member.status && member.status.toLowerCase().includes(query))
          );
        });
      }

      // 4) Apply rating filter:
      if (this.selectedRating) {
        const [min, max] = this.selectedRating.split("-").map(Number);
        filtered = filtered.filter((member) => {
          const rating = member.averageRating;
          return typeof rating === "number" && rating >= min && rating <= max;
        });
      }

      // 5) Apply *one* sort if activeFilters.filter is set:
      if (this.activeFilters.filter) {
        filtered.sort((a, b) => {
          let valA, valB;
          const ascending = this.activeFilters.filter_ascending;

          switch (this.activeFilters.filter) {
            case "name":
              valA = (a.name || "").toLowerCase();
              valB = (b.name || "").toLowerCase();
              return ascending
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);

            case "averageRating":
              valA = a.averageRating || 0;
              valB = b.averageRating || 0;
              return ascending ? valA - valB : valB - valA;

            case "reviewCount":
              valA = a.reviewCount || 0;
              valB = b.reviewCount || 0;
              return ascending ? valA - valB : valB - valA;

            case "profile_strength":
              valA = a.profile_strength || 0;
              valB = b.profile_strength || 0;
              return ascending ? valA - valB : valB - valA;

            default:
              return 0;
          }
        });
      }

      // Return the fully filtered (and sorted) array
      return filtered;
    },
    paginatedMembers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredMembers.slice(start, end); // Read-only computed property
    },
    totalPages() {
      return Math.ceil(this.filteredMembers.length / this.itemsPerPage);
    },
    platformStats() {
      const stats = {};
      this.invitedMembers.forEach((member) => {
        if (member.connected_apps && Array.isArray(member.connected_apps)) {
          member.connected_apps.forEach((platform) => {
            stats[platform] = (stats[platform] || 0) + 1;
          });
        }
      });

      return Object.entries(stats)
        .map(([platform, count]) => ({ platform, count }))
        .sort((a, b) => b.count - a.count);
    },
    hasActiveFilters() {
      return Object.values(this.activeFilters).some((value) => value !== null);
    },
    selectedSortDescription() {
      return this.selectedSortOption ? this.selectedSortOption.text : "Default";
    },

    currentUser() {
      return this.$store.getters.currentUser;
    },
    authUser() {
      return this.$store.getters.authUser;
    },
    WID() {
      const wid = this.$store.getters.WID;
      return wid;
    },
    currentUID() {
      const uid = this.$store.getters.currentUID;
      return uid;
    },
    // Fix this computed property to ensure it's properly accessing the Vuex store
    globalStores() {
      return this.$store.getters.globalStores || [];
    },
  },
  methods: {
    // Add the fetchPlatformLogo method right at the beginning of methods
    async fetchPlatformLogo(platform) {
      if (this.platformLogoCache[platform]) {
        return;
      }

      try {
        console.log("Fetching platform logo for:", platform);
        const platformDoc = await getDoc(
          doc(db, "fly_platforms", platform.toLowerCase())
        );
        if (platformDoc.exists()) {
          const logoUrl = platformDoc.data().logo_url;
          this.$set(this.platformLogoCache, platform, logoUrl);
          console.log("Found logo URL for", platform, logoUrl);
        } else {
          console.log("No platform document found for:", platform);
        }
      } catch (error) {
        console.error("Error fetching platform logo:", error);
      }
    },

    async checkIfShallowBusinessesExist() {
      try {
        const collectionRef = query(
          collection(db, "users", this.currentUID, "fly_workspaces"),
          where("storeWorkspace", "==", true),
          limit(1) // We only need to check if at least one exists
        );
        const querySnapshot = await getDocs(collectionRef);
        this.hasShallowBusinesses = !querySnapshot.empty;
        if (this.hasShallowBusinesses) {
          this.availableStatuses.push("shallow");
        }
      } catch (error) {
        console.log("Error checking for store workspaces:", error);
        this.hasShallowBusinesses = false;
      }
    },
    clearZoneError() {
      this.zoneError = "";
    },
    toggleSelectAll() {
      // Update all businesses' selected state based on selectAll value
      this.filteredBusinesses.forEach((business) => {
        if (!business.disabled) {
          this.$set(business, "selected", this.selectAll);
        }
      });
    },

    async addBusinessLocation(business) {
      this.$loader.show();
      const existingWorkspaceQuery = query(
        collection(db, "users", this.currentUID, "fly_workspaces"),
        where("placeId", "==", business.place_id),
        where("admin", "==", this.WID)
      );

      const existingWorkspaceSnapshot = await getDocs(existingWorkspaceQuery);

      if (!existingWorkspaceSnapshot.empty) {
        this.$alert.show(
          "This business location is already added to your workspaces."
        );
        this.$loader.hide();
        return;
      }
      this.$set(business, "isAdding", true);
      const created_at = new Date();
      const memberData = {
        ...business,
        created_at,
        storeWorkspace: true,
        storeLocation: `${business.name}, ${business.address}`,
        name: business.name,
        title: business.name,
        placeId: business.place_id,
        geoLocation: business.location
          ? {
              latitude: business.location.lat,
              longitude: business.location.lng,
            }
          : null,
        admin: this.WID,
        connectType: "shallow",
        shallowConnect: true,
        sub_locality: business.addressComponents?.area || null,
        city: business.city || business.addressComponents?.city || null,
        state: business.state || business.addressComponents?.state || null,
        country:
          business.country || business.addressComponents?.country || null,
        address: business.address,
        gmb_shallow_profile_data: { ...business.gmb_shallow_profile_data },
        gmbProfile: { ...business.gmbProfile },
        connected_apps: ["google_business_profile"], // Initialize with Google Business Profile
      };
      console.log("memberData", memberData);
      try {
        const docRef = await addDoc(
          collection(db, "users", this.currentUID, "fly_workspaces"),
          memberData
        );
        console.log("docRef", docRef);
        let storeUserWID = docRef.id;
        console.log("storeUserWID", storeUserWID);
        await updateDoc(docRef, { id: docRef.id });
        const doc_id = `${this.currentUID}_${this.WID}`;

        // Update shallow status
        const statusRef = doc(db, "fly_shallow_status", doc_id);
        const statusDoc = await getDoc(statusRef);

        if (statusDoc.exists()) {
          await updateDoc(statusRef, {
            total_locations: increment(1),
            updated_at: new Date(),
            error_notification_sent: null,
            locations: arrayUnion({
              wid: storeUserWID,
              rank_tracker_status: "in_progress",
              review_status: "in_progress",
              name: memberData.name,
              email_status: "pending",
              admin_email: this.authUser?.email,
            }),
          });
        } else {
          await setDoc(statusRef, {
            uid: this.currentUID,
            admin_wid: this.WID,
            total_locations: 1,
            processed_locations: 0,
            status: "in_progress",
            created_at: new Date(),
            updated_at: new Date(),
            admin_email: this.authUser?.email,
            error_notification_sent: null,
            locations: [
              {
                wid: storeUserWID,
                rank_tracker_status: "in_progress",
                review_status: "in_progress",
                name: memberData.name,
                email_status: "pending",
                admin_email: this.authUser?.email,
              },
            ],
          });
        }

        this.$set(business, "disabled", true);
        this.$set(business, "selected", true);
        this.$snackbar.show(`Successfully added 1 location`);
        this.selectAll = false;
      } catch (error) {
        console.error("Error adding business location:", error);
      } finally {
        this.$loader.hide();
      }
    },
    async addSelectedLocations() {
      const selectedBusinesses = this.filteredBusinesses.filter(
        (business) => business.selected && !business.disabled
      );
      if (!selectedBusinesses.length) return;
      let wids = [];

      try {
        for (const business of selectedBusinesses) {
          // Set loading state for this specific business
          this.$set(business, "isAdding", true);

          const created_at = new Date();
          const memberData = {
            ...business,
            created_at,
            storeWorkspace: true,
            storeLocation: `${business.name}, ${business.address}`,
            name: business.name,
            title: business.name,
            placeId: business.place_id,
            geoLocation: business.location
              ? {
                  latitude: business.location.lat,
                  longitude: business.location.lng,
                }
              : null,
            admin: this.WID,
            connectType: "shallow",
            shallowConnect: true,
            sub_locality: business.addressComponents?.area || null,
            city: business.city || business.addressComponents?.city || null,
            state: business.state || business.addressComponents?.state || null,
            country:
              business.country || business.addressComponents?.country || null,
            address: business.address,
            gmb_shallow_profile_data: { ...business.gmb_shallow_profile_data },
            gmbProfile: { ...business.gmbProfile },
            connected_apps: ["google_business_profile"], // Initialize with Google Business Profile
          };
          // Add the document to Firestore
          const docRef = await addDoc(
            collection(db, "users", this.currentUID, "fly_workspaces"),
            memberData
          );
          console.log(this.authUser, "docRef id", docRef.id);
          let storeUserWID = docRef.id;
          wids.push(storeUserWID);
          const doc_id = `${this.currentUID}_${this.WID}`;
          // Update the document with its ID
          await updateDoc(docRef, { id: docRef.id });
          this.$set(business, "disabled", true);
          this.$set(business, "selected", true);
          const statusRef = doc(db, "fly_shallow_status", doc_id);
          const statusDoc = await getDoc(statusRef);
          if (statusDoc.exists()) {
            await updateDoc(statusRef, {
              total_locations: increment(1),
              updated_at: new Date(),
              error_notification_sent: null,
              locations: arrayUnion({
                wid: storeUserWID,
                rank_tracker_status: "in_progress",
                review_status: "in_progress",
                name: memberData.name,
                email_status: "pending",
                admin_email: this.authUser?.email,
              }),
            });
          } else {
            await setDoc(statusRef, {
              uid: this.currentUID,
              admin_wid: this.WID,
              status: "in_progress",
              created_at: new Date(),
              updated_at: new Date(),
              admin_email: this.authUser?.email,
              error_notification_sent: null,
              locations: [
                {
                  wid: storeUserWID,
                  rank_tracker_status: "in_progress",
                  review_status: "in_progress",
                  name: memberData.name,
                  email_status: "pending",
                  admin_email: this.authUser?.email,
                },
              ],
            });
          }
        }
        // update the workspace doc with businessSearchTerm along with locations (wids) - it should be added to track locations added for a business
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists()) {
          await updateDoc(workspaceRef, {
            swot_brands: arrayUnion({
              business_name: this.businessSearchTerm,
              locations: wids,
              status: "in_progress",
              payload: {
                businessName: this.businessSearchTerm,
                region: this.selectedZone,
                country: this.selectedCountry,
                locations: wids,
                instance: window.location.hostname,
                uid: this.currentUID,
                admin_wid: this.WID,
                trace_id: uuidv4(),
              },
            }),
          });
        }
        this.$snackbar.show(
          `Successfully added ${selectedBusinesses.length} locations`
        );

        // Clear selections
        this.selectAll = false;
      } catch (error) {
        console.error("Error adding selected locations:", error);
        this.$alert.show("Failed to add some locations. Please try again.");
      } finally {
        this.$loader.hide();
      }
    },
    async setupScrapingResultsListener(jobId) {
      try {
        // Reference to the job document
        const jobRef = doc(db, "fly_scraping_jobs", jobId);
        // Set up listener for job status
        const jobUnsubscribe = onSnapshot(jobRef, async (snapshot) => {
          if (!snapshot.exists()) {
            console.error("Job not found");
            return;
          }
          const jobData = snapshot.data();
          if (jobData.status === "completed") {
            try {
              // Get all documents from results collection
              const resultsRef = collection(jobRef, "results");
              const resultsSnapshot = await getDocs(resultsRef);

              // Map to store unique businesses
              const businessMap = new Map();

              // Process all documents
              resultsSnapshot.docs.forEach((doc) => {
                const businessData = doc.data();
                const mapId = businessData.map_id;
                if (!mapId) {
                  console.warn("Business document missing map_id:", doc.id);
                  return;
                }
                // Format business data
                const formattedBusiness = {
                  ...businessData,
                  reviews:
                    businessData.gmb_shallow_profile_data.reviews ||
                    businessData.reviews ||
                    null,
                  isAdding: false,
                  selected: false,
                  disabled: false,
                };
                // Store in map (this automatically handles duplicates)
                businessMap.set(mapId, formattedBusiness);
              });
              // Update searchResults with unique businesses
              this.searchResults = Array.from(businessMap.values());
              console.log("searchResults", this.searchResults);
              this.$snackbar.show(
                `Successfully fetched ${this.searchResults.length} locations`
              );
              // Update UI state
              this.isSearching = false;
              this.hasSearched = true;
              this.isBusinessLoading = false;
              // Clean up listeners
              if (this.resultsUnsubscribe) {
                this.resultsUnsubscribe();
                this.resultsUnsubscribe = null;
              }
              if (this.jobUnsubscribe) {
                this.jobUnsubscribe();
                this.jobUnsubscribe = null;
              }
            } catch (error) {
              console.error("Error setting up results listener:", error);
            }
          } else if (jobData.status === "in_progress") {
            this.isBusinessLoading = true;
            // If job is still in progress, set up real-time listener for results
            if (!this.resultsUnsubscribe) {
              this.setupResultsListener(jobRef);
            }
          }
        });
        this.jobUnsubscribe = jobUnsubscribe;
      } catch (error) {
        console.error("Error setting up listeners:", error);
        this.isSearching = false;
        this.hasSearched = true;
      }
    },
    setupResultsListener(jobRef) {
      const resultsRef = collection(jobRef, "results");
      // Track businesses by map_id to prevent duplicates
      const businessMap = new Map();
      // Set up listener for results collection
      this.resultsUnsubscribe = onSnapshot(
        resultsRef,
        (snapshot) => {
          // Handle only new or modified documents
          snapshot.docChanges().forEach((change) => {
            const businessData = change.doc.data();
            const mapId = businessData.map_id;
            if (!mapId) {
              console.warn("Business document missing map_id:", change.doc.id);
              return;
            }
            if (change.type === "added" || change.type === "modified") {
              // Format the business data
              /* 
                        keyword,
            title: place.title || "",
            name: place.title || "",
            description: place.description || "",
            summary: place.summary || "",
            claimed: place.claimed || "",
            address: place.address || "",
            rating: place.rating || "",
            user_ratings_total: place.reviews_cnt || "",
            location: {
              lat: place.latitude || "",
              lng: place.longitude || "",
            },
            business_status: "",
            categories: place.category,
            primary_category: primaryCategory,
            additional_categories: additionalCategories,
            phone: place.phone || "",
            link: place.link || "",
            display_link: place.display_link || "",
            map_id: place.map_id,
            placeId: place.map_id_encoded,
            map_id_encoded: place.map_id_encoded,
            uid,
            wid
            */
              const formattedBusiness = {
                ...businessData,
                reviews:
                  businessData.gmb_shallow_profile_data.reviews ||
                  businessData.reviews ||
                  null,
                isAdding: false,
                selected: false,
                disabled: false,
              };
              // Update existing or add new business
              const existingIndex = this.searchResults.findIndex(
                (b) => b.map_id === mapId
              );
              if (existingIndex !== -1) {
                this.$set(this.searchResults, existingIndex, {
                  ...this.searchResults[existingIndex],
                  ...formattedBusiness,
                });
              } else {
                businessMap.set(mapId, formattedBusiness);
                this.searchResults.push(formattedBusiness);
              }
            }
          });
        },
        (error) => {
          console.error("Error in results listener:", error);
        }
      );
      console.log("results listener set up", this.searchResults);
    },
    async searchBusinesses() {
      if (
        !this.businessSearchTerm ||
        !this.selectedZone ||
        !this.selectedCountry
      ) {
        if (!this.selectedZone) {
          this.zoneError = "Please select a zone";
        }
        if (!this.selectedCountry) {
          this.countryError = "Please select a country";
        }
        return;
      }
      this.zoneError = ""; // Clear error if validation passes
      this.countryError = ""; // Clear error if validation passes
      try {
        const fetchBusinesses = httpsCallable(
          functions,
          "fetchGMBShallowBusinessDetails"
        );
        try {
          this.$loader.show();
          this.isSearching = true;
          this.hasSearched = false;
          // Clear previous results and listeners
          this.searchResults = [];
          if (this.jobUnsubscribe) {
            this.jobUnsubscribe();
            this.jobUnsubscribe = null;
          }
          if (this.resultsUnsubscribe) {
            this.resultsUnsubscribe();
            this.resultsUnsubscribe = null;
          }
          const response = await fetchBusinesses({
            query: this.businessSearchTerm,
            uid: this.currentUID,
            wid: this.WID,
            zone: this.selectedZone,
            country: this.selectedCountry,
            type: "shallow",
            maxResults: this.maxResults,
          });
          // const data = await response.json();
          // console.log("response", response.data);
          const jobId = response.data.jobId;
          this.isBusinessLoading = true;
          this.$snackbar.show(response.data.message);
          await this.setupScrapingResultsListener(jobId);
        } catch (error) {
          console.error("Error fetching businesses:", error);
          // this.hasSearched = false;
        } finally {
          this.isSearching = false;
          this.hasSearched = true;
          this.$loader.hide();
        }
      } catch (error) {
        console.error("Error loading more results:", error);
      }
    },
    applySort(option) {
      this.selectedSortOption = option;
      this.activeFilters.filter = option.field;
      this.activeFilters.filter_ascending = option.ascending;
      // The filteredMembers computed property will automatically re-sort.
    },
    isProfileDataLoading(member) {
      if (!member.status_trail) {
        return false;
      }
      // Get the business_details array from the status_trail map
      const businessDetails = member.status_trail.business_details;
      // If no business_details entries, not loading
      if (
        !businessDetails ||
        !Array.isArray(businessDetails) ||
        businessDetails.length === 0
      ) {
        return false;
      }
      // Sort entries by timestamp (newest first)
      const sortedEntries = [...businessDetails].sort((a, b) => {
        const tsA = a.ts
          ? typeof a.ts === "string"
            ? new Date(a.ts)
            : a.ts.toDate()
          : 0;
        const tsB = b.ts
          ? typeof b.ts === "string"
            ? new Date(b.ts)
            : b.ts.toDate()
          : 0;
        return tsB - tsA;
      });
      // Check if the latest status is 'in progress'
      return sortedEntries[0].status === "in progress";
    },
    profile_strength(member) {
      const location = member.gmbProfile;
      const shallow_profile = member.gmb_shallow_profile_data;
      const fields = {
        description: !!location?.profile?.description,
        websiteUri: shallow_profile
          ? !!shallow_profile?.website
          : !!location?.websiteUri,
        primaryCategory: shallow_profile
          ? !!shallow_profile?.primary_category
          : !!location?.categories?.primaryCategory?.displayName,
        additionalCategories: shallow_profile
          ? !!(shallow_profile?.additional_categories?.length > 0)
          : !!(location?.categories?.additionalCategories?.length > 0),
        regularHours: shallow_profile
          ? !!(shallow_profile?.opening_hours?.length > 0)
          : !!(location?.regularHours?.periods?.length > 0),
        phoneNumber: shallow_profile
          ? !!shallow_profile?.phone
          : !!location?.phoneNumbers?.primaryPhone,
        // :white_check_mark: Extended checks (deep + shallow compatible)
        // openDate: shallow_profile
        //   ? !!shallow_profile?.opening_date
        //   : !!location?.openInfo?.openingDate,
        // postCount: shallow_profile
        //   ? (shallow_profile?.post_count || 0) > 0
        //   : (location?.postCount || 0) > 0,
        hasLogo: shallow_profile
          ? !!shallow_profile?.logo
          : !!location?.mediaItems?.logo,
        hasCoverPhoto: shallow_profile
          ? !!shallow_profile?.cover_photo
          : !!location?.mediaItems?.coverPhoto,
        hasPhotos: shallow_profile
          ? (shallow_profile?.photos_length || 0) > 0
          : (location?.mediaItems?.photosLength || 0) > 0,
        attributeCount: shallow_profile
          ? (shallow_profile?.attribute_count || 0) > 0
          : (location?.attributeCount || 0) > 0,
        hasSocialLinks: shallow_profile
          ? shallow_profile?.has_social_links === true
          : location?.socialInfo?.found === true,
      };
      const totalFields = Object.keys(fields).length; // Will be 7
      const filledFields = Object.values(fields).filter(Boolean).length;
      const total = Math.round((filledFields / totalFields) * 100);
      // return {
      //   total,
      //   fields
      // };
      return total;
    },
    initAutocomplete() {
      const input = this.$refs.autocomplete;
      if (!input) {
        console.warn("Autocomplete input element not found");
        return;
      }

      // The PlaceAutocompleteElement will handle input events.
      // input.addEventListener("input", (e) => {
      //   this.storeLocation = e.target.value;
      //   this.name = e.target.value;
      // });

      // Check if Google Maps API is available
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.warn("Google Maps Places API not loaded");
        return;
      }

      // Ensure the input is a HTMLElement for PlaceAutocompleteElement
      if (!(input instanceof HTMLElement)) {
        console.error("Autocomplete input is not a valid HTML Element.");
        return;
      }

      const autocompleteElement =
        new window.google.maps.places.PlaceAutocompleteElement({
          inputElement: input,
          // No setFields method; fields are requested on place selection or via place.fetchFields()
          // types: ['geocode'], // Or other types as needed, establishment was used before.
          // For general addresses, 'geocode' or ['address'] might be more suitable.
          // If it's for businesses, ['establishment'] is correct.
          // Assuming general address input here based on form fields.
          fields: ["address_components", "geometry", "name"], // These are fields to request for the selected place.
        });

      // The PlaceAutocompleteElement is a web component and might need to be added to the DOM
      // or the existing input might need to be replaced by it if `inputElement` doesn't cover it.
      // Assuming for now it enhances the existing input.

      autocompleteElement.addEventListener("gmp-placeselect", async (event) => {
        const place = event.place;
        if (!place || !place.id) return; // Check for place and place.id

        // Fetch the specific fields needed
        await place.fetchFields({
          fields: ["addressComponents", "geometry", "name", "formattedAddress"],
        });

        if (!place.geometry) return;

        this.city = "";
        this.state = "";
        this.zipcode = "";
        this.country = "";
        this.sub_locality = "";
        this.storeLocation = ""; // Will be repopulated
        this.geoLocation = "";

        const addressComponents = place.addressComponents;
        if (addressComponents) {
          addressComponents.forEach((component) => {
            const types = component.types;
            if (types.includes("sublocality_level_2")) {
              this.sub_locality = component.longText; // Or .longName based on new API
            }
            if (
              this.sub_locality == "" &&
              (types.includes("sublocality_level_1") ||
                types.includes("sublocality")) // More general sublocality
            ) {
              this.sub_locality = component.longText;
            }
            if (types.includes("locality")) {
              this.city = component.longText;
            }
            if (types.includes("administrative_area_level_1")) {
              this.state = component.longText;
            }
            if (types.includes("postal_code")) {
              this.zipcode = component.longText;
            }
            if (types.includes("country")) {
              this.country = component.longText;
            }
          });
        }

        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          this.geoLocation = `${lat},${lng}`;
        }

        this.name =
          place.name ||
          (place.addressComponents &&
            place.addressComponents.find((c) => c.types.includes("premise"))
              ?.longText) ||
          "";

        // Reconstruct storeLocation from the new Place object fields
        // place.formattedAddress is often a good starting point.
        this.storeLocation =
          place.formattedAddress ||
          [
            this.name, // Use the extracted name
            this.sub_locality,
            this.city,
            this.state,
            this.zipcode,
            this.country,
          ]
            .filter(Boolean)
            .join(", ")
            .replace(/,{2,}/g, ",")
            .replace(/(^,)|(,$)/g, "")
            .trim();

        // Also update this.name if it was part of storeLocation and not set directly from place.name
        if (!this.name && this.storeLocation.includes(",")) {
          this.name = this.storeLocation.split(",")[0];
        } else if (!this.name) {
          this.name = this.storeLocation;
        }
      });
    },

    // Function to generate an invitation token and store it in Firestore
    async generateInvitationToken(uid, wid) {
      const token = `${uid}-${wid}-${Date.now()}`; // Unique token
      const expiresAt = Timestamp.fromDate(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Expires in 7 days
      );
      const docRef = doc(db, "users", uid, "fly_workspaces", wid);

      await updateDoc(docRef, { token_expires_at: expiresAt });

      return token;
    },
    async addMember() {
      const created_at = new Date();
      const memberData = {
        created_at,
        storeWorkspace: true,
        storeLocation: this.storeLocation,
        geoLocation: this.geoLocation,
        sub_locality: this.sub_locality,
        city: this.city,
        state: this.state,
        zipcode: this.zipcode,
        name: this.name || this.storeLocation,
        country: this.country,
        email: this.sendInvitation ? this.email || null : null,
        phone: this.sendInvitation ? this.phone || null : null,
        status: this.sendInvitation ? this.invitationStatus : "accepted",
        admin: this.WID,
      };

      try {
        const docRef = await addDoc(
          collection(db, "users", this.currentUID, "fly_workspaces"),
          memberData
        );

        let storeUserWID = docRef.id;
        await updateDoc(docRef, { id: docRef.id });

        // Only send invitation if checkbox is checked and either email or phone exists
        if (this.sendInvitation && (this.email || this.phone)) {
          const token = await this.generateInvitationToken(
            this.currentUID,
            storeUserWID
          );
          const sendMemberInvitation = httpsCallable(
            functions,
            "sendInvitationToStore"
          );
          try {
            await sendMemberInvitation({
              email: this.email || null,
              phone: this.phone || null,
              userName: this.name || this.storeLocation,
              workspaceName: this.name || this.storeLocation,
              wid: storeUserWID,
              uid: this.currentUID,
              token,
              admin:
                this.currentUser?.display_name ||
                this.currentUser?.email ||
                this.currentUser?.phone_number ||
                "",
              domain: window.location.host,
            });
            this.$snackbar.show(
              "Location added and invitation sent successfully"
            );
          } catch (error) {
            console.error("Error sending invitation:", error);
            this.$alert.show(
              "Location added but failed to send invitation. You can resend it later."
            );
          }
        } else {
          this.$snackbar.show("Location added successfully");
        }
        this.fetchStores();
        this.resetForm();
      } catch (error) {
        console.error("Error adding location:", error);
        this.$alert.show("Failed to add location. Please try again.");
      }
    },
    async openInvitationLink(member) {
      const baseUrl = window.location.origin;
      const formattedWorkspaceName = member.name?.replace(/\s+/g, "_") || "";
      let admin =
        this.currentUser?.display_name ||
        this.currentUser?.email ||
        this.currentUser?.phone_number ||
        "";

      let formattedAdmin = admin;
      if (!admin.includes("@") && !admin.match(/^\+?[0-9\s]+$/)) {
        formattedAdmin = admin.replace(/\s+/g, "_");
      } else if (admin.match(/^\+?[0-9\s]+$/)) {
        formattedAdmin = admin.replace(/\s+/g, "");
      }
      const token = await this.generateInvitationToken(
        this.currentUID,
        member.id
      );
      const workspaceDocRef = doc(
        db,
        "users",
        this.currentUID,
        "fly_workspaces",
        member.id
      );
      const workspaceSnap = await getDoc(workspaceDocRef);
      if (workspaceSnap.exists && workspaceSnap.data().status == "invited") {
        await updateDoc(workspaceDocRef, {
          status: "accepted",
        });
      }
      window.location.href =
        "/store-invitation-accepted?uid=" +
        this.currentUID +
        "&wid=" +
        member.id +
        "&self=true&token=" +
        token +
        "&workspace_name=" +
        formattedWorkspaceName +
        "&sender=" +
        formattedAdmin;
      // this.$router.push(
      // "/store-invitation-accepted?uid=" +
      // this.currentUID +
      // "&wid=" +
      // member.id +
      // "&self=true" +
      // "&token=" +
      // token +
      // "&workspace_name=" +
      // formattedWorkspaceName +
      // "&sender=" +
      // formattedAdmin
      // );
      // const invitationLink = `${baseUrl}/accept-invite?self=true&uid=${this.currentUID}&wid=${member.id}&workspace_name=${formattedWorkspaceName}&sender=${formattedAdmin}&token=${token}`;

      // Open in new tab
      // window.location.href = invitationLink;
      // window.open(invitationLink, '_blank');
    },
    async resendInvitation(member) {
      // Add checks for email/phone
      if (!member.email && !member.phone) {
        this.$alert.show(
          "No contact information available to send invitation."
        );
        return;
      }

      const token = await this.generateInvitationToken(
        this.currentUID,
        member.id
      );
      const sendMemberInvitation = httpsCallable(
        functions,
        "sendInvitationToStore"
      );

      try {
        await sendMemberInvitation({
          email: member.email || null,
          phone: member.phone || null,
          userName: member.name || member.storeLocation,
          workspaceName: member.name || member.storeLocation,
          wid: member.id,
          uid: this.currentUID,
          token,
          admin:
            this.currentUser?.display_name ||
            this.currentUser?.email ||
            this.currentUser?.phone_number ||
            "",
          domain: window.location.host,
        });
        this.$snackbar.show("Invitation sent successfully");
      } catch (error) {
        console.error("Error sending invitation:", error);
        this.$alert.show("Failed to send invitation. Please try again later.");
      }
    },
    handleMapsCloseBtn() {
      this.mapsSearchForm = false;
      this.resetMapsForm();
    },
    resetMapsForm() {
      this.selectedCountry = "";
      this.businessSearchTerm = "";
      this.selectedZone = "";
      this.countryError = "";
      this.zoneError = "";
      this.maxResults = null;
    },

    resetForm() {
      this.storeLocation = "";
      this.sub_locality = "";
      this.city = "";
      this.state = "";
      this.zipcode = "";
      this.country = "";
      this.name = "";
      this.email = "";
      this.phone = "";
      this.invitationStatus = "invited";
      this.$refs.autocomplete.value = "";
      this.geoLocation = "";
      this.addingNew = false;
      this.sendInvitation = false;
    },
    async deleteMember(member) {
      let uid = this.currentUID || this.currentUID;
      this.$confirm.show({
        message: `Are you sure you want to delete this location?`,
        onConfirm: async () => {
          if (member.shallowConnect) {
            // set the delete request in firestore to delete shallow location's data
            await setDoc(
              doc(db, "fly_shallow_locations_delete_requests", member.id),
              {
                uid: uid,
                wid: member.id,
                place_id: member.place_id,
                created_at: new Date(),
                status: "in_progress",
              }
            );
          }
          const workspaceDocRef = doc(
            db,
            "users",
            uid,
            "fly_workspaces",
            member.id
          );

          // Recursive function to delete subcollections
          const deleteSubcollections = async (parentDocRef) => {
            const subcollections = [
              "connected-apps",
              "social-posts",
              "connected-apps-token",
              "keywords",
              "team",
            ]; // Add other subcollections if needed
            for (const subcollectionName of subcollections) {
              const subcollectionRef = collection(
                parentDocRef,
                subcollectionName
              );
              const subcollectionDocs = await getDocs(subcollectionRef);

              for (const docSnap of subcollectionDocs.docs) {
                // Call deleteSubcollections recursively if there are nested subcollections
                await deleteSubcollections(
                  doc(db, parentDocRef.path, subcollectionName, docSnap.id)
                );
                // Delete the subcollection document
                await deleteDoc(docSnap.ref);
                console.log(`Deleted document in subcollection: ${docSnap.id}`);
              }
            }
          };

          //Delete gmb unique locations
          const gmbDocRef = doc(
            db,
            "users",
            uid,
            "fly_workspaces",
            member.id,
            "connected-apps",
            "google_business_profile"
          );

          const gmbDocSnap = await getDoc(gmbDocRef);
          if (gmbDocSnap.exists()) {
            // If GMB doc exists, get the location ID
            const gmbData = gmbDocSnap.data();
            if (
              gmbData.accounts &&
              gmbData.accounts[0]?.locations &&
              gmbData.accounts[0].locations[0]?.name
            ) {
              const locationId = gmbData.accounts[0].locations[0].name;
              // Process location ID (e.g., delete related data in Firestore)
              const cleanedLocationId = locationId.replace(/^locations\//, "");

              // Delete the location from unique socials (if necessary)
              const uniqueSocialPageRef = doc(
                db,
                "unique_socials",
                "google_business_profile",
                "locations",
                cleanedLocationId
              );
              const uniqueSocialSnap = await getDoc(uniqueSocialPageRef);
              if (uniqueSocialSnap.exists()) {
                const socialDocData = uniqueSocialSnap.data();
                if (
                  socialDocData?.uid === uid &&
                  socialDocData?.wid === member.id
                ) {
                  console.log("Document found. UID and WID match. Deleting...");
                  await deleteDoc(uniqueSocialPageRef);
                  console.log("Document deleted successfully.");
                } else {
                  console.log(
                    "UID and WID do not match. Deletion skipped. Document UID:",
                    socialDocData?.uid,
                    ", WID:",
                    socialDocData?.wid
                  );
                }
              }
            } else {
              console.log("No valid location found in GMB document.");
            }
          } else {
            console.log("GMB document does not exist.");
          }
          // Delete all subcollections first
          await deleteSubcollections(workspaceDocRef);
          // Now delete the workspace document itself
          await deleteDoc(workspaceDocRef);
          this.fetchStores();
        },
      });
    },
    editMember(index) {
      this.editedIndex = index;
      this.editedMember = { ...this.invitedMembers[index] };
      this.dialog = true;
    },
    updateMember() {
      if (this.editedIndex !== null) {
        this.invitedMembers.splice(this.editedIndex, 1, this.editedMember);
        this.closeDialog();
      }
    },
    closeDialog() {
      this.dialog = false;
      this.editedMember = {};
      this.editedIndex = null;
    },
    async fetchStores() {
      this.isLoading = true;
      try {
        // Use the global stores from Vuex
        if (this.globalStores && this.globalStores.length > 0) {
          console.log(
            "Using globalStores from Vuex:",
            this.globalStores.length
          );
          this.processStores(this.globalStores);
        } else {
          console.log(
            "No globalStores found, attempting to refresh global stores"
          );

          // Try to refresh global stores first by calling the method in AppHeader
          try {
            // Find the AppHeader component
            const appHeaderRef = document.querySelector('[ref="appHeader"]');
            if (appHeaderRef && appHeaderRef.__vue__) {
              console.log(
                "Found AppHeader component, trying to refresh globalStores"
              );
              await appHeaderRef.__vue__.fetchGlobalStores();

              // Check if that worked
              if (this.globalStores && this.globalStores.length > 0) {
                console.log(
                  "Successfully refreshed globalStores:",
                  this.globalStores.length
                );
                this.processStores(this.globalStores);
                return;
              }
            } else {
              console.log("AppHeader component not found");
            }
          } catch (err) {
            console.error("Error calling AppHeader.fetchGlobalStores:", err);
          }

          // Fallback: fetch directly from Firestore
          console.log("Falling back to direct Firestore query");
          try {
            const storesRef = collection(
              db,
              "users",
              this.currentUID,
              "fly_workspaces"
            );
            const storesQuery = query(
              storesRef,
              where("admin", "==", this.WID),
              where("storeWorkspace", "==", true)
            );
            const storesSnapshot = await getDocs(storesQuery);

            console.log(
              "Direct Firestore query returned:",
              storesSnapshot.size,
              "docs"
            );
            const stores = storesSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            if (stores.length > 0) {
              console.log(
                "Using stores directly fetched from Firestore:",
                stores.length
              );
              // Update the Vuex store
              this.$store.commit("SET_GLOBAL_STORES", stores);
              this.processStores(stores);
            } else {
              console.log("No stores found in Firestore");
              this.invitedMembers = [];
              this.totalMembers = 0;
            }
          } catch (error) {
            console.error("Error fetching stores from Firestore:", error);
            this.invitedMembers = [];
            this.totalMembers = 0;
          }
        }
      } catch (error) {
        console.error("Error in fetchStores:", error);
        this.invitedMembers = [];
        this.totalMembers = 0;
      } finally {
        this.isLoading = false;
      }
    },
    downloadSampleCSV() {
      const headers = [
        "brand",
        "sub_locality",
        "city",
        "state",
        "zipcode",
        "country",
        "email",
        "phone",
      ];

      const sampleData = [
        [
          "Supermarket A",
          "Bandra West",
          "Mumbai",
          "Maharashtra",
          "400050",
          "India",
          "store1@example.com",
          "1234567890",
        ],
      ];

      try {
        const csvContent = [
          headers.join(","),
          ...sampleData.map((row) => row.join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "sample_locations.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download error:", error);
        this.$alert.show("Error downloading sample CSV");
      }
    },
    formatPhoneForWhatsApp(phone) {
      if (!phone) return "";

      // Remove all non-numeric characters
      const cleanPhone = phone.replace(/\D/g, "");

      // If the number doesn't start with '+' or country code, assume it's Indian
      if (!cleanPhone.startsWith("91") && !cleanPhone.startsWith("1")) {
        return "91" + cleanPhone;
      }

      return cleanPhone;
    },
    addFilter(type, value) {
      // If clicking the same filter value, remove it
      if (this.activeFilters[type] === value) {
        this.removeFilter(type);
        return;
      }
      console.log(type, "typeval filter", value);

      // Add new filter
      this.$set(this.activeFilters, type, value);
    },
    removeFilter(type) {
      this.$set(this.activeFilters, type, null);
    },
    async handleBulkAdd() {
      try {
        this.$snackbar.show("Please wait a moment");
        this.isLoading = true;

        // Use the same initConnection method from the mixin
        await this.initConnection("google_business_profile", "bulk");
        setTimeout(() => {
          this.$snackbar.hide();
        }, 2000);
      } catch (error) {
        this.isLoading = false;
        console.error("Error starting bulk import:", error);
        this.$alert.show("Failed to start bulk import. Please try again.");
      } finally {
        this.isLoading = false;
        this.isDropdownOpen = false;
      }
    },

    handleBulkAddClick() {
      this.activeTab = 1;
      this.handleBulkAdd();
    },

    // Add a new method to force refresh from Firestore
    async forceRefreshStores() {
      this.isLoading = true;
      try {
        // Always fetch directly from Firestore first to ensure up-to-date data
        console.log("Force refreshing stores from Firestore directly");
        const storesRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces"
        );
        const storesQuery = query(
          storesRef,
          where("admin", "==", this.WID),
          where("storeWorkspace", "==", true)
        );
        const storesSnapshot = await getDocs(storesQuery);

        console.log(
          "Direct Firestore query returned:",
          storesSnapshot.size,
          "docs"
        );
        const stores = storesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (stores.length > 0) {
          console.log(
            "Using stores directly fetched from Firestore:",
            stores.length
          );
          // Update the Vuex store to keep it in sync
          this.$store.commit("SET_GLOBAL_STORES", stores);
          this.processStores(stores);
        } else {
          console.log("No stores found in Firestore");
          this.invitedMembers = [];
          this.totalMembers = 0;
        }
      } catch (error) {
        console.error("Error in forceRefreshStores:", error);

        // Fallback to normal fetch if direct fetch fails
        await this.fetchStores();
      } finally {
        this.isLoading = false;
      }
    },

    closeInviteForm() {
      this.showInviteForm = false;
      this.resetForm(); // Optional: reset the form when closing
    },
    processStores(stores) {
      this.isLoading = true;
      try {
        console.log("Processing stores:", stores.length);
        this.totalMembers = stores.length;
        const members = stores.map((storeData) => {
          let memberData = { ...storeData };
          memberData.phone = memberData?.gmb_shallow_profile_data
            ? memberData?.gmb_shallow_profile_data?.phone
            : memberData?.gmbProfile?.phoneNumbers?.primaryPhone;
          memberData.website = memberData?.gmb_shallow_profile_data
            ? memberData?.gmb_shallow_profile_data?.website
            : memberData?.gmbProfile?.websiteUri;
          memberData.averageRating = memberData?.gmb_shallow_profile_data
            ? memberData?.gmb_shallow_profile_data?.rating
            : memberData.averageRating || 0;
          memberData.reviewCount = memberData?.gmb_shallow_profile_data
            ? memberData?.gmb_shallow_profile_data?.reviews
            : memberData.reviewCount || 0;
          memberData.profile_strength = this.profile_strength(memberData);
          memberData.connectType = memberData.shallowConnect
            ? "shallow"
            : "deep";
          memberData.name = memberData?.gmb_shallow_profile_data
            ? memberData?.gmb_shallow_profile_data?.title
            : memberData?.gmbProfile?.title || "";
          memberData.isProfileDataLoading =
            this.isProfileDataLoading(memberData);

          // Add google_business_profile to connected_apps if GMB data exists
          if (
            (memberData.gmb_shallow_profile_data || memberData.gmbProfile) &&
            (!memberData.connected_apps ||
              !memberData.connected_apps.includes("google_business_profile"))
          ) {
            // Initialize connected_apps array if it doesn't exist
            if (!memberData.connected_apps) {
              memberData.connected_apps = [];
            }
            // Add google_business_profile if not already present
            if (
              !memberData.connected_apps.includes("google_business_profile")
            ) {
              memberData.connected_apps.push("google_business_profile");
            }
          }

          return memberData;
        });

        console.log("Processed", members.length, "locations");
        this.invitedMembers = members;

        // Force fetch platform logos immediately, especially for GMB
        this.fetchAllPlatformLogos();
      } catch (error) {
        console.error("Error processing stores:", error);
        this.invitedMembers = [];
        this.totalMembers = 0;
      } finally {
        this.isLoading = false;
      }
    },
    filterByStatus(status) {
      this.activeTab = 1; // Switch to Locations tab
      this.selectedStatus = status; // Set the selected status
      // The filteredMembers computed property will automatically update
      this.$nextTick(() => {
        const tableSection = document.querySelector(".table-section");
        if (tableSection) {
          tableSection.scrollIntoView({ behavior: "smooth" });
        }
      });
    },
    nextPage() {
      if (this.currentPage < Math.ceil(this.totalMembers / this.itemsPerPage)) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== "text/csv") {
        this.$alert.show("Please upload a CSV file");
        return;
      }

      this.isUploading = true;
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const csvContent = e.target.result;
          const uploadLocationsCSV = httpsCallable(
            functions,
            "uploadLocationsCSV"
          );

          const response = await uploadLocationsCSV({
            csvData: csvContent,
            uid: this.currentUID,
            wid: this.WID,
          });

          this.uploadResults = response.data;
          this.isUploading = false;

          if (response.data.success > 0) {
            this.fetchStores(); // Refresh the locations list
          }
        } catch (error) {
          console.error("Error processing CSV:", error);
          this.isUploading = false;
          this.uploadResults = {
            success: 0,
            skipped: 0,
            errors: 1,
            errorDetails: [error.message || "Failed to process CSV file"],
          };
        }
      };

      reader.onerror = () => {
        this.isUploading = false;
        this.$alert.show("Error reading file");
      };

      reader.readAsText(file);
    },
    // Add a new helper method to specifically fetch all platform logos, especially GMB
    fetchAllPlatformLogos() {
      // Always pre-fetch Google Business Profile logo
      this.fetchPlatformLogo("google_business_profile");

      // Then fetch logos for all connected apps across all locations
      this.invitedMembers.forEach((member) => {
        if (member.connected_apps && Array.isArray(member.connected_apps)) {
          member.connected_apps.forEach((platform) => {
            this.fetchPlatformLogo(platform);
          });
        }
      });
    },

    // Handle location profile view
    handleViewProfile(member) {
      this.selectedLocationId = member.id;
      this.showLocationProfileDrawer = true;
    },
  },
  watch: {
    invitedMembers: {
      immediate: true,
      handler(members) {
        members.forEach((member) => {
          if (member.connected_apps) {
            member.connected_apps.forEach((platform) => {
              this.fetchPlatformLogo(platform);
            });
          }
        });
      },
    },
    searchQuery: "handleSearch", // Watch for changes in searchQuery
    selectedCountry(newValue) {
      // Reset selected zone when country changes
      this.selectedZone = "";
      // Update zones based on selected country
      if (newValue === "us") {
        this.zones = [
          { text: "Northeast", value: "northeast" },
          { text: "Southeast", value: "southeast" },
          { text: "Midwest", value: "midwest" },
          { text: "West", value: "west" },
          { text: "Southwest", value: "southwest" },
        ];
      } else {
        // Default zones for other countries
        this.zones = [
          { text: "North", value: "north" },
          { text: "South", value: "south" },
          { text: "East", value: "east" },
          { text: "West", value: "west" },
          { text: "Central", value: "central" },
        ];
      }
    },
    globalStores: {
      immediate: true,
      handler(newStores) {
        if (newStores && newStores.length > 0) {
          this.processStores(newStores);
        }
      },
    },
  },
  beforeDestroy() {
    // Cleanup listeners when component is destroyed
    if (this.jobUnsubscribe) {
      this.jobUnsubscribe();
    }
    if (this.resultsUnsubscribe) {
      this.resultsUnsubscribe();
    }
  },

  // Add activated hook for keep-alive components
  activated() {
    console.log("Location component activated");
    // When returning to this page, always force refresh the stores
    this.isLoading = true;
    this.forceRefreshStores();
  },

  mounted() {
    console.log("Location component mounted, currentUID:", this.currentUID);

    if (
      (auth.currentUser && auth.currentUser.uid !== this.currentUID) ||
      this.currentWorkspace?.storeWorkspace
    ) {
      this.$snackbar.show("Direct access is prohibited");
      this.$router.push("/calendar");
      return;
    }

    // Set loading state right away to prevent flashing NoLocationsConnected
    this.isLoading = true;

    this.checkIfShallowBusinessesExist();

    // Ensure Google Maps is loaded before initializing autocomplete
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log("Google Maps Places API loaded");
        this.initAutocomplete();
      } else {
        console.log("Waiting for Google Maps Places API to load...");
        // If not loaded yet, try again after a short delay
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();

    // Pre-fetch GMB platform logo right away
    this.fetchPlatformLogo("google_business_profile");

    // Log global stores state before fetching
    console.log(
      "Global stores state before fetch:",
      this.globalStores ? `${this.globalStores.length} stores` : "not available"
    );

    // Use forceRefreshStores to ensure fresh data when returning to the page
    this.forceRefreshStores();
  },
};
</script>

<style scoped>
/* Container Styling */
.invite-section {
  margin-top: 1.5rem;
  background-color: #f9fafb; /* Very light gray */
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.invite-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group,
.form-row {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.input-field {
  flex: 1;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 7px;
  width: 100%;
  font-size: 16px;
  box-sizing: border-box;
  word-wrap: break-word;
  /* Allow long words to break */
  white-space: normal;
  /* Allow text to wrap */
}

.btn-primary {
  background-color: #4316a2;
  color: #000000;
  border-radius: 7px;
  padding: 10px 20px;
  transition: background-color 0.3s ease;
  cursor: pointer;
  align-self: flex-start;
}

.btn-primary:hover {
  color: #fff;
  background-color: #5b29c1;
}

.table-section {
  width: 100%;
}

.header-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  /* Align items to the left */
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
}

.search-section {
  width: 100%;
  margin-bottom: 1rem;
}

.search-filters-container {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  flex-wrap: wrap;
}

.search-container {
  flex: 0 0 300px;
  /* Fixed width for search box */
}

.status-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ccc;
  border-radius: 7px;
  font-size: 16px;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  border-color: #8056dd;
  outline: none;
}

.table-responsive {
  /* height: calc(200vh - 600px); */
  /* min-height: 600px; */
  overflow-y: auto;
  overflow-x: hidden;
}
/* Show horizontal scroll only on mobile */
@media (max-width: 768px) {
  .table-responsive {
    overflow-x: auto;
  }
}

/* Ensure icons don't shrink */

/* Status styles */
.status-invited,
.status-accepted,
.status-connected,
.status-disconnected {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-results {
  text-align: center;
  /* Align text to the left */
  padding: 20px;
  color: #555;
  font-size: 16px;
}

/* Action Icons Styling */
.action-icon {
  cursor: pointer;
  font-size: 20px;
}

.action-icon:hover {
  opacity: 0.7;
}

/* Responsive Styling */

/* Tablet and Below */
@media (max-width: 1024px) {
  .title {
    font-size: 22px;
  }

  .input-field {
    font-size: 15px;
    padding: 8px;
  }

  .btn-primary {
    padding: 8px 16px;
    font-size: 15px;
  }

  .action-icon {
    font-size: 18px;
  }
}

/* Mobile Devices */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }

  .invite-form {
    gap: 10px;
  }

  .input-field {
    font-size: 14px;
    padding: 8px;
  }

  .btn-primary {
    width: 100%;
    padding: 10px;
    font-size: 16px;
  }

  .action-icon {
    font-size: 16px;
  }

  .search-filters-container {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-container {
    flex: none;
    width: 100%;
  }

  .status-filters {
    justify-content: flex-start;
  }
}

/* Small Mobile Devices */
@media (max-width: 450px) {
  .table-responsive {
    font-size: 14px;
  }

  .btn-primary {
    font-size: 14px;
    padding: 8px;
  }

  .search-container {
    max-width: 100%;
    padding: 0 10px;
  }
}

/* Extra Small Mobile Devices */
@media (max-width: 400px) {
  .table-responsive {
    font-size: 12px;
  }

  .btn-primary {
    font-size: 12px;
    padding: 6px;
  }
}

.platform-logos img {
  transition: transform 0.2s;
}

.platform-logos img:hover {
  transform: scale(1.1);
}

.stats-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
}

.stat-item {
  display: flex;
  align-items: center;
  color: #4a5568;
}

.status-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-filter-btn {
  padding: 0.3rem 0.6rem; /* Slightly adjusted padding */
  border: 1px solid #e2e8f0; /* slate-200 */
  border-radius: 0.375rem; /* rounded-md */
  font-size: 0.75rem; /* text-xs */
  font-weight: 500; /* medium */
  transition: all 0.2s;
  background-color: white;
  color: #475569; /* slate-600 */
}

.status-filter-btn:hover {
  border-color: #cbd5e0; /* slate-300 */
  background-color: #f8fafc; /* slate-50 */
}

.status-filter-btn.active {
  font-weight: 600; /* semibold */
  background-color: #f5f3ff; /* purple-50 */
  border-color: #7a58d5; /* secondary color (bright purple) */
  color: #7a58d5;
}

/* Specific status active states - ensure light background, colored text/border */
.status-filter-btn.status-invited.active {
  background-color: #f5f3ff; /* purple-50 */
  border-color: #7a58d5; /* secondary (bright purple) */
  color: #7a58d5;
}

.status-filter-btn.status-accepted.active {
  background-color: #fffbeb; /* amber-50 */
  border-color: #f59e0b; /* amber-500 */
  color: #f59e0b;
}

.status-filter-btn.status-connected.active {
  background-color: #f0fdf4; /* green-50 */
  border-color: #22c55e; /* green-500 */
  color: #22c55e;
}

.status-filter-btn.status-disconnected.active {
  background-color: #fef2f2; /* red-50 */
  border-color: #ef4444; /* red-500 */
  color: #ef4444;
}

.status-filter-btn.status-shallow.active {
  background-color: #eff6ff; /* blue-50 */
  border-color: #3b82f6; /* blue-500 */
  color: #3b82f6;
}

.v-dialog {
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.upload-progress {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.active-filters {
  background-color: #f8fafc;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background-color: #8050de;
  color: white;
  border-radius: 9999px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.filter-chip button {
  opacity: 0.8;
  transition: opacity 0.2s;
}

.filter-chip button:hover {
  opacity: 1;
}

.filter-chip:hover {
  background-color: #6040ae;
}

/* Status Filter Buttons */
.status-filter-btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  background-color: white;
}

.status-filter-btn:hover {
  background-color: #f7fafc;
}

.status-filter-btn.active {
  font-weight: 600;
}

/* Status colors for filter buttons */
.status-filter-btn.status-invited.active {
  background-color: #f3f0ff;
  border-color: #8050de;
  color: #8050de;
}

.status-filter-btn.status-accepted.active {
  background-color: #fff3e0;
  border-color: #ff9800;
  color: #ff9800;
}

.status-filter-btn.status-connected.active {
  background-color: #e8f5e9;
  border-color: #4caf50;
  color: #4caf50;
}

.status-filter-btn.status-disconnected.active {
  background-color: #ffebee;
  border-color: #f30000;
  color: #f30000;
}

.status-filter-btn.status-shallow.active {
  background-color: #f7feff;
  border-color: #48a6a7;
  color: #48a6a7;
}

/* Status Badge Styles */
.status-badge {
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.status-invited {
  background-color: #f3f0ff;
  color: #8050de;
  border: 1px solid #8050de;
}

.status-accepted {
  background-color: #fff3e0;
  color: #ff9800;
  border: 1px solid #ff9800;
}

.status-connected {
  background-color: #e8f5e9;
  color: #4caf50;
  border: 1px solid #4caf50;
}

.status-disconnected {
  background-color: #ffebee;
  color: #f30000;
  border: 1px solid #f30000;
}

/* Ensure status badges don't wrap */
td .status-badge {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Add some spacing between status and platform icons */
td .status-badge + div {
  margin-top: 0.375rem;
}

/* Make sure platform icons stay on one line if possible */
td .platform-icons {
  display: flex;
  gap: 0.375rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE/Edge */
}

td .platform-icons::-webkit-scrollbar {
  display: none;
  /* Chrome/Safari/Opera */
}

/* Responsive adjustments */
@media (max-width: 1280px) {
  .status-filter-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.813rem;
  }

  .status-badge {
    padding: 0.188rem 0.5rem;
    font-size: 0.75rem;
  }
}

@media (max-width: 768px) {
  .status-filter-btn {
    padding: 0.188rem 0.375rem;
    font-size: 0.75rem;
  }
}

.address-cell {
  white-space: normal;
  line-height: 1.4;
}

.address-cell .flex {
  gap: 0.25rem;
}

.address-cell a {
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

/* Separator dot styling */
.address-cell .text-gray-400 {
  display: inline-block;
  margin: 0 0.125rem;
  font-weight: bold;
  line-height: 1;
}

/* Responsive adjustments */
@media (max-width: 1280px) {
  .address-cell a {
    max-width: 120px;
  }

  .address-cell {
    font-size: 0.813rem;
  }
}

@media (max-width: 1024px) {
  .address-cell a {
    max-width: 100px;
  }
}

@media (max-width: 768px) {
  .address-cell .flex {
    flex-direction: column;
    gap: 0.125rem;
  }

  .address-cell .text-gray-400 {
    display: none;
  }

  .address-cell a {
    max-width: none;
  }
}

.form-checkbox {
  margin: 1rem 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-input {
  width: 1.2rem;
  height: 1.2rem;
  cursor: pointer;
}

.checkbox-text {
  font-size: 0.95rem;
  color: #4a5568;
}

/* Style disabled inputs */
.input-field:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

.gold-star {
  color: gold;
}

/* Rating Filter Dropdown */
.rating-filter-dropdown {
  padding: 8px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background-color: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.rating-filter-dropdown:hover {
  background-color: #f7fafc;
}

/* Center the pagination controls */
.pagination-controls {
  display: flex;
  /* Use flexbox for alignment */
  justify-content: center;
  /* Center the buttons horizontally */
  margin-top: 3rem;
  /* Add some space above the pagination controls */
}

.pagination-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 5px;
  background-color: #8050de;
  /* Primary color */
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  margin: 0 0.5rem;
  /* Space between buttons */
}

.pagination-button:hover {
  background-color: #6a3fbd;
  /* Darker shade on hover */
  transform: scale(1.05);
  /* Slightly enlarge on hover */
}

.pagination-button.disabled {
  background-color: #ccc;
  /* Gray color for disabled state */
  cursor: not-allowed;
  /* Change cursor for disabled state */
}

.loading-message {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #555;
}

.rating-filter-v-select.v-text-field--outlined
  .v-input__control
  .v-input__slot {
  min-height: 30px !important; /* Adjust height to be compact */
  padding: 0 8px !important;
}
.rating-filter-v-select .v-select__selection--comma {
  font-size: 0.75rem; /* text-xs */
  margin: 2px 4px 2px 0;
}
.rating-filter-v-select .v-label {
  font-size: 0.75rem; /* text-xs */
}
.rating-filter-v-select .v-input__append-inner .v-icon {
  font-size: 16px; /* Smaller dropdown icon */
}

/* Form transition styles */
.form-fade-enter-active,
.form-fade-leave-active {
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}
.form-fade-enter,
.form-fade-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}
</style>
